import { useState } from "react";
import { INVITE_TTL_DAYS } from "../../shared/devices/sharing.ts";
import { useAppEnvironment } from "../../shared/environment.tsx";
import { BottomSheet } from "../../shared/ui/BottomSheet/BottomSheet.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import styles from "./InviteSheet.module.css";

/**
 * Шторка «Пригласить пользователя» (Overlay / Send Invite, Figma 1053:27362). В макете invite уходит на email,
 * а backend выдаёт ссылку: она уходит через системное «Поделиться», без него — копируется в буфер обмена.
 */
export function InviteSheet({ link, onClose }: { link: string | null; onClose: () => void }) {
  return (
    <BottomSheet open={link !== null} onClose={onClose} title="Пригласить пользователя">
      {link && <InviteLink link={link} onDone={onClose} />}
    </BottomSheet>
  );
}

type ShareState = "idle" | "sharing" | "copied" | "copyFailed";

function InviteLink({ link, onDone }: { link: string; onDone: () => void }) {
  const { share, copyText } = useAppEnvironment();
  const [state, setState] = useState<ShareState>("idle");

  // Окно «Поделиться» открывается только по жесту пользователя, поэтому — по кнопке, а не сразу после создания.
  async function send() {
    setState("sharing");
    if (share) {
      try {
        await share({ title: "Приглашение в E-va", text: "Присоединяйтесь к моей E-va", url: link });
        setState("idle");
        return;
      } catch (error) {
        // Пользователь закрыл окно — это не ошибка. Иначе браузер отказал: остаётся буфер обмена.
        if (error instanceof DOMException && error.name === "AbortError") {
          setState("idle");
          return;
        }
      }
    }
    try {
      await copyText(link);
      setState("copied");
    } catch {
      setState("copyFailed");
    }
  }

  return (
    <div className={styles.body}>
      <div className={styles.text}>
        <p className={styles.title}>Ссылка-приглашение готова</p>
        <p className={styles.link}>{link}</p>
        <p className={styles.hint}>
          Её можно принять один раз в течение {INVITE_TTL_DAYS} дней. Гость сможет писать на эту E-va.
        </p>
        {state === "copied" && (
          <p className={styles.hint} role="status">
            Ссылка скопирована
          </p>
        )}
        {state === "copyFailed" && (
          <p className={styles.error} role="alert">
            Не удалось скопировать. Выделите ссылку и скопируйте её вручную
          </p>
        )}
      </div>
      <div className={styles.actions}>
        <Button loading={state === "sharing"} onClick={() => void send()}>
          {share ? "Поделиться ссылкой" : "Скопировать ссылку"}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Готово
        </Button>
      </div>
    </div>
  );
}
